import type { Property } from '~/core/entities/property';
import type { ClientPagination } from './pagination';

interface PropertyReturn {
	properties: ComputedRef<Property[]>;
	addProperties(properties: Property[]): void;
	needMoreProperties(): boolean;
	movePropertiesFromCached(): void;
	reset(): void;
}

/**
 * Represents a computed property that tracks pagination state.
 * @param pagination - The pagination data for managing properties.
 * @returns - Exposes properties and methods for managing property state.
 */
export function Property(
	pagination: ComputedRef<ClientPagination>,
	perPageFirstPage: number = 10,
	perPageNextPages: number = 12
): PropertyReturn {
	/**
	 * State for storing properties and cached properties persisted between server and client.
	 */
	const _propertiesState = useState<{
		_properties: Property[];
		_cachedProperties: Property[];
	}>('_properties-listing', () => ({
		_properties: [],
		_cachedProperties: []
	}));
	/*
	 * The properties computed value returns the current properties.
	 */
	const properties = computed(() => _propertiesState.value._properties);
	/**
	 * Sets the cached properties.
	 * @private
	 * @param properties - The properties to set as cached.
	 */
	function _setCachedProperties(properties: Property[]): void {
		_propertiesState.value._cachedProperties = properties;
	}
	/**
	 * Resets the cached properties
	 * @private
	 */
	function _resetCachedProperties(): void {
		_propertiesState.value._cachedProperties = [];
	}
	/**
	 * Sets the properties.
	 * @private
	 * @param properties - The properties to set.
	 */
	function _setProperties(properties: Property[]): void {
		_propertiesState.value._properties = properties;
	}
	/**
	 * Resets the properties.
	 * @private
	 */
	function _resetProperties(): void {
		_propertiesState.value._properties = [];
	}
	/**
	 * Checks if the current page is the first page.
	 * @returns - Whether the current page is the first page.
	 */
	function _isFirstPage(): boolean {
		return pagination.value.page === 1;
	}
	/**
	 * Checks if more properties are needed to be fetched.
	 * @returns - Whether more properties are needed to be fetched.
	 */
	function needMoreProperties(): boolean {
		const hasLessCachedPropertiesThanNeeded =
			_propertiesState.value._cachedProperties.length <
			pagination.value.perPage;

		const hasLessStoredPropertiesThanAvailable =
			_propertiesState.value._cachedProperties.length +
				_propertiesState.value._properties.length <
			pagination.value.filteredCount;

		return (
			hasLessCachedPropertiesThanNeeded && hasLessStoredPropertiesThanAvailable
		);
	}
	/**
	 * Adds properties to the current properties.
	 * @param properties - The properties to add.
	 */
	function addProperties(properties: Property[]): void {
		const cachedProperties = [
			..._propertiesState.value._cachedProperties,
			...properties
		];

		const perPage = _isFirstPage() ? perPageFirstPage : perPageNextPages;

		const newProperties = _isFirstPage()
			? cachedProperties.slice(0, perPage)
			: [
					..._propertiesState.value._properties,
					...cachedProperties.slice(0, perPageNextPages)
				];

		const newCachedProperties = cachedProperties.slice(perPage);

		_setCachedProperties(newCachedProperties);
		_setProperties(newProperties);
	}
	/**
	 * Moves properties from cached properties to properties.
	 * This should be user for client next page navigation.
	 */
	function movePropertiesFromCached(): void {
		const newProperties: Property[] = [
			..._propertiesState.value._properties,
			..._propertiesState.value._cachedProperties.slice(0, perPageNextPages)
		];

		const newCachedProperties: Property[] =
			_propertiesState.value._cachedProperties.slice(perPageNextPages);

		_setProperties(newProperties);
		_setCachedProperties(newCachedProperties);
	}
	/**
	 * Resets the properties and cached properties.
	 */
	function reset(): void {
		_resetProperties();
		_resetCachedProperties();
	}

	return {
		properties,
		addProperties,
		needMoreProperties,
		movePropertiesFromCached,
		reset
	};
}
